import type { ReactNode } from "react";
import { Badge } from "../../badge";
import { formatDate, formatDateTime } from "../../format";
import { Calendar, Clock } from "../../icons";
import type { ArticleMetaModel } from "./types";

export interface ArticleMetaLineProps {
  meta: ArticleMetaModel;
  /** Reading-time in minutes (the shell computes it from a markdown body). */
  minutes?: number;
}

/**
 * The one article metadata line: author · date · reading-time · provenance ·
 * tags, `·`-separated. Dates go through the shared `formatDate` (never inline).
 */
export function ArticleMetaLine({ meta, minutes }: ArticleMetaLineProps) {
  const bits: ReactNode[] = [];
  if (meta.author) bits.push(<span key="a">{meta.author}</span>);
  if (meta.date) {
    bits.push(
      <span key="d" className="flex items-center gap-1">
        <Calendar className="h-3.5 w-3.5" />
        {meta.dateLabel ? `${meta.dateLabel} ` : ""}
        {meta.dateTime ? formatDateTime(meta.date) : formatDate(meta.date)}
      </span>,
    );
  }
  const mins = meta.readingMinutes ?? minutes;
  if (mins) {
    bits.push(
      <span key="r" className="flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" />
        {mins} min read
      </span>,
    );
  }
  if (meta.tail) {
    bits.push(
      <span key="t" className="font-mono type-subtext">
        {meta.tail}
      </span>,
    );
  }

  const hasTags = !!meta.tags && meta.tags.length > 0;
  if (bits.length === 0 && !hasTags) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 type-body text-muted-foreground">
      {bits.map((b, i) => (
        <span key={i} className="flex items-center gap-3">
          {i > 0 ? <span className="text-border">·</span> : null}
          {b}
        </span>
      ))}
      {hasTags ? (
        <span className="flex items-center gap-1.5">
          {meta.tags!.map((t) => (
            <Badge key={t} variant="secondary">
              {t}
            </Badge>
          ))}
        </span>
      ) : null}
    </div>
  );
}
